import { useEffect, useRef } from "react";
import { ErrorComponent, type ErrorComponentProps } from "@tanstack/react-router";
import { Button } from "@/components/ui/Button";
import { chunkRecovery, isChunkLoadError, useChunkRecoveryPhase } from "@/lib/chunkRecovery";
import { t } from "@/lib/strings";

// The router's default error component (lib/router.tsx). A lazy chunk of an old build
// that a deploy deleted used to surface as TanStack Router's raw red box ("Something went
// wrong! [Hide Error] Failed to fetch dynamically imported module: …"). Such failures now
// get a German explanation and a "Neu laden" button; every other error keeps the
// router's default box unchanged.
export function RouteErrorFallback(props: ErrorComponentProps) {
  const chunk = isChunkLoadError(props.error);

  // Vite's `vite:preloadError` normally started the recovery already (main.tsx); this
  // covers dynamic imports that fail without that event. recover() is idempotent.
  useEffect(() => {
    if (chunk) chunkRecovery.recover();
  }, [chunk]);

  if (!chunk) return <ErrorComponent {...props} />;
  return <ChunkErrorScreen />;
}

function ChunkErrorScreen() {
  const phase = useChunkRecoveryPhase();
  // The message replaces the page the user was working in, so the element that had the
  // focus is gone: keyboard and screen-reader users land on the one action.
  const reloadRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    reloadRef.current?.focus();
  }, []);
  return (
    <div
      role="alert"
      style={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "var(--bg)",
        padding: 48,
      }}
    >
      <div style={{ textAlign: "center", maxWidth: 440 }}>
        <h1
          style={{
            margin: 0,
            fontSize: 22,
            fontWeight: 600,
            color: "var(--ink-1)",
            letterSpacing: "-0.01em",
          }}
        >
          {t.chunkErrorTitle}
        </h1>
        <p
          style={{ fontSize: 13.5, color: "var(--ink-3)", margin: "10px 0 22px", lineHeight: 1.5 }}
        >
          {phase === "reloading" ? t.chunkErrorReloading : t.chunkErrorBody}
        </p>
        <Button
          ref={reloadRef}
          variant="primary"
          size="md"
          icon="refresh"
          onClick={() => chunkRecovery.reloadNow()}
        >
          {t.reloadPage}
        </Button>
      </div>
    </div>
  );
}
